import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services } from '../data/mock';

const Services = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-animate').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const serviceDetails = [
    {
      service: services[0],
      features: ['React, Angular & Vue.js', 'RESTful APIs', 'Responsive Design', 'Performance Optimization'],
      color: 'from-blue-600 to-cyan-600'
    },
    {
      service: services[1],
      features: ['Native iOS & Android', 'Cross-platform Development', 'App Store Deployment', 'Push Notifications'],
      color: 'from-purple-600 to-pink-600'
    },
    {
      service: services[2],
      features: ['RESTful API Design', 'Third-party Integration', 'API Documentation', 'Microservices Architecture'],
      color: 'from-green-600 to-teal-600'
    },
    {
      service: services[3],
      features: ['AWS & Azure', 'Docker & Kubernetes', 'CI/CD Pipelines', 'Infrastructure as Code'],
      color: 'from-orange-600 to-red-600'
    },
    {
      service: services[4],
      features: ['Custom ERP Systems', 'Workflow Automation', 'Legacy System Modernization', 'Enterprise Integration'],
      color: 'from-indigo-600 to-blue-600'
    },
    {
      service: services[5],
      features: ['User Research', 'Wireframing & Prototyping', 'Visual Design', 'Usability Testing'],
      color: 'from-pink-600 to-rose-600'
    },
    {
      service: services[6],
      features: ['Data Transformation', 'Quality Assurance', 'ETL Processes', 'Data Pipeline Development'],
      color: 'from-cyan-600 to-blue-600'
    },
    {
      service: services[7],
      features: ['N8N & Zapier Integration', 'Process Automation', 'Workflow Optimization', 'No-Code Solutions'],
      color: 'from-yellow-600 to-orange-600'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden pt-20">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div
          className="absolute top-20 right-20 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        ></div>
        <div
          className="absolute bottom-20 left-20 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl"
          style={{ transform: `translateY(${-scrollY * 0.2}px)` }}
        ></div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 text-center z-10">
          <h1 className="text-5xl md:text-6xl font-light tracking-tight text-white mb-6">
            Our <span className="font-semibold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Services</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Comprehensive software development solutions designed to accelerate your digital transformation and drive business growth
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-24">
            {serviceDetails.map((item, index) => {
              const IconComponent = LucideIcons[item.service.icon];
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={item.service.id}
                  className={`flex flex-col ${
                    isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  } items-center gap-12 scroll-animate opacity-0`}
                >
                  {/* Service Card */}
                  <div className="flex-1 w-full">
                    <Card className="border-0 shadow-2xl hover:shadow-3xl transition-all duration-300 bg-white overflow-hidden group">
                      <CardContent className="p-10">
                        <div className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                          {IconComponent && <IconComponent className="text-white" size={32} />}
                        </div>
                        <h2 className="text-3xl font-semibold text-gray-900 mb-4">
                          {item.service.title}
                        </h2>
                        <p className="text-gray-600 text-lg leading-relaxed mb-8">
                          {item.service.description}
                        </p>
                        <div className="space-y-3">
                          {item.features.map((feature, idx) => (
                            <div key={idx} className="flex items-start space-x-3 group/item">
                              <CheckCircle2 className={`bg-gradient-to-r ${item.color} bg-clip-text text-transparent flex-shrink-0 mt-1`} size={20} />
                              <span className="text-gray-700 group-hover/item:text-gray-900 transition-colors">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Visual Element with Pattern */}
                  <div className="flex-1 w-full flex items-center justify-center">
                    <div className={`relative w-full max-w-md h-80 bg-gradient-to-br ${item.color} rounded-3xl transform hover:scale-105 transition-all duration-500 shadow-2xl overflow-hidden group`}>
                      <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        {IconComponent && (
                          <div className="relative">
                            <IconComponent className="text-white/90 group-hover:scale-110 transition-transform duration-500" size={140} strokeWidth={1.5} />
                            <div className="absolute inset-0 bg-white/20 blur-3xl rounded-full"></div>
                          </div>
                        )}
                      </div>
                      {/* Decorative elements */}
                      <div className="absolute top-10 right-10 w-20 h-20 border-4 border-white/30 rounded-xl rotate-12 group-hover:rotate-45 transition-transform duration-500"></div>
                      <div className="absolute bottom-10 left-10 w-16 h-16 border-4 border-white/30 rounded-full group-hover:scale-125 transition-transform duration-500"></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 scroll-animate opacity-0">
            <h2 className="text-4xl md:text-5xl font-light text-white mb-4">
              Our <span className="font-semibold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Process</span>
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              A streamlined approach to delivering exceptional results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', description: 'Understanding your needs and goals', color: 'from-blue-600 to-cyan-600' },
              { step: '02', title: 'Planning', description: 'Creating a detailed roadmap', color: 'from-purple-600 to-pink-600' },
              { step: '03', title: 'Development', description: 'Building with precision and care', color: 'from-green-600 to-teal-600' },
              { step: '04', title: 'Launch', description: 'Deploying and supporting your solution', color: 'from-orange-600 to-red-600' }
            ].map((process, index) => (
              <Card
                key={index}
                className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white/10 backdrop-blur-md scroll-animate opacity-0"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-8">
                  <div className={`text-6xl font-bold bg-gradient-to-r ${process.color} bg-clip-text text-transparent mb-4`}>
                    {process.step}
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-3">{process.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{process.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-600 to-cyan-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-light mb-6 scroll-animate opacity-0">
            Let's Build Something <span className="font-semibold">Amazing</span>
          </h2>
          <p className="text-lg text-blue-100 mb-12 leading-relaxed scroll-animate opacity-0">
            Schedule a free consultation to discuss your project requirements
          </p>
          <Button
            asChild
            size="lg"
            className="bg-white hover:bg-gray-100 text-blue-600 px-8 py-6 text-base transition-all duration-300 hover:scale-105 shadow-xl scroll-animate opacity-0"
          >
            <Link to="/contact">
              Get Started Today
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Services;